import React, { useState, useEffect } from 'react';
import { useRecordContext, useDataProvider, useNotify, useTranslate } from 'react-admin';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  CircularProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Pagination,
  Stack
} from '@mui/material';
import {
  ExpandMore,
  SwapVert,
  Send,
  CallReceived,
  Error as ErrorIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  CheckCircle,
  BatteryChargingFull,
  ContentCopy,
  Code,
  DataObject,
  ArrowForward,
} from '@mui/icons-material';
import { IconButton, Tooltip } from '@mui/material';
import { format } from 'date-fns';

// Log level icon mapping
const LogLevelIcon = ({ level }: { level: string }) => {
  switch (level?.toLowerCase()) {
    case 'error':
    case 'fatal':
      return <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />;
    case 'warn':
    case 'warning':
      return <WarningIcon sx={{ color: 'warning.main', fontSize: 20 }} />;
    case 'info':
      return <InfoIcon sx={{ color: 'info.main', fontSize: 20 }} />;
    case 'debug':
      return <CheckCircle sx={{ color: 'success.main', fontSize: 20 }} />;
    default:
      return <InfoIcon sx={{ color: 'grey.500', fontSize: 20 }} />;
  }
};

// OCPP message type badge
const OcppMessageBadge = ({ messageType, action }: { messageType?: string; action?: string }) => {
  if (!messageType && !action) return null;

  const label = messageType || action || 'OCPP';
  const colors: Record<string, any> = {
    'StartTransaction': 'success',
    'StopTransaction': 'error',
    'MeterValues': 'primary',
    'StatusNotification': 'info',
    'Authorize': 'warning',
    'Heartbeat': 'default',
  };

  const color = colors[label] || 'default';

  return (
    <Chip
      label={label}
      color={color as any}
      size="small"
      icon={<SwapVert />}
      sx={{ mr: 1 }}
    />
  );
};

// Direction badge
const DirectionBadge = ({ direction }: { direction?: string }) => {
  const translate = useTranslate();
  if (!direction) return null;

  const isOutgoing = direction === 'outgoing' || direction === 'sent';

  return (
    <Chip
      label={translate(isOutgoing ? 'resources.charging-sessions.show.outgoing' : 'resources.charging-sessions.show.incoming')}
      size="small"
      variant="outlined"
      icon={isOutgoing ? <Send /> : <CallReceived />}
      color={isOutgoing ? 'success' : 'info'}
    />
  );
};

// Connector status color mapping
const connectorStatusColors: Record<string, string> = {
  'Available': '#4caf50',
  'Preparing': '#ff9800',
  'Charging': '#2196f3',
  'SuspendedEV': '#ed6c02',
  'SuspendedEVSE': '#ed6c02',
  'Finishing': '#9c27b0',
  'Faulted': '#d32f2f',
  'Unavailable': '#757575',
  'Reserved': '#0288d1',
};

const ConnectorStatusChip = ({ status }: { status: string }) => (
  <Chip
    label={status}
    size="small"
    sx={{
      backgroundColor: connectorStatusColors[status] || '#757575',
      color: '#fff',
      fontWeight: 600,
      fontSize: '0.7rem',
      height: 22,
    }}
  />
);

// Extract connector status info from a log entry
const getStatusInfo = (log: any, allLogs?: any[]): { previous?: string; current?: string } | null => {
  const data = log._source?.data;
  if (!data) return null;

  if (data.new_status) {
    return { previous: data.previous_status, current: data.new_status };
  }

  if (data.ocpp_action === 'StatusNotification' && data.payload?.status) {
    return { current: data.payload.status };
  }

  // For CALL_RESULT of StatusNotification, look up the matched CALL's status
  if (data.ocpp_message_type === 3 && allLogs) {
    const matchingCall = findMatchingCall(allLogs, log);
    if (matchingCall?._source?.data?.ocpp_action === 'StatusNotification') {
      const callPayload = matchingCall._source.data.payload;
      if (callPayload?.status) {
        return { current: callPayload.status };
      }
    }
  }

  return null;
};

// OCPP Message section with raw/beautify toggle and copy
const OcppMessageSection = ({ messageArray }: { messageArray: string }) => {
  const [beautified, setBeautified] = useState(false);
  const [copied, setCopied] = useState(false);

  const getDisplayContent = () => {
    if (!beautified) return messageArray;
    try {
      const parsed = JSON.parse(messageArray);
      return JSON.stringify(parsed, null, 2);
    } catch {
      return messageArray;
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(messageArray);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
        <Tooltip title={beautified ? 'Show raw string' : 'Beautify JSON'}>
          <IconButton size="small" onClick={() => setBeautified(!beautified)}>
            {beautified ? <Code fontSize="small" /> : <DataObject fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title={copied ? 'Copied!' : 'Copy raw message'}>
          <IconButton size="small" onClick={handleCopy}>
            <ContentCopy fontSize="small" color={copied ? 'success' : 'inherit'} />
          </IconButton>
        </Tooltip>
      </Box>
      <Box
        component="pre"
        sx={{
          p: 1,
          backgroundColor: 'grey.100',
          borderRadius: 1,
          overflow: 'auto',
          maxHeight: 400,
          maxWidth: '100%',
          whiteSpace: beautified ? 'pre' : 'pre-wrap',
          wordBreak: 'break-all',
          fontSize: '0.75rem',
          m: 0,
        }}
      >
        {getDisplayContent()}
      </Box>
    </Box>
  );
};

// Helper function to find the matching CALL for a CALL_RESULT
const findMatchingCall = (logs: any[], currentLog: any): any | null => {
  const data = currentLog._source?.data;
  if (!data || data.ocpp_message_type !== 3) return null; // Not a CALL_RESULT

  const messageId = data.ocpp_message_id;
  if (!messageId) return null;

  // Search backwards from current log to find matching CALL with same message ID
  const currentIndex = logs.findIndex(l => l.id === currentLog.id);
  if (currentIndex === -1) return null;

  for (let i = currentIndex - 1; i >= 0; i--) {
    const prevData = logs[i]._source?.data;
    if (prevData?.ocpp_message_type === 2 && prevData?.ocpp_message_id === messageId) {
      return logs[i];
    }
  }

  return null;
};

// Helper function to extract key info from OCPP payload
const getPayloadSummary = (log: any, allLogs?: any[]): string | null => {
  const data = log._source?.data;
  if (!data) return null;

  // Try to get payload from various sources
  let payload = data.payload || data.response_payload;

  // If no payload, try parsing from ocpp_message_array
  if (!payload && data.ocpp_message_array) {
    try {
      const messageArray = JSON.parse(data.ocpp_message_array);
      // OCPP message format: [MessageTypeId, MessageId, Action, Payload]
      // For CALL (type 2): [2, messageId, action, payload]
      // For CALLRESULT (type 3): [3, messageId, payload]
      if (Array.isArray(messageArray)) {
        if (messageArray[0] === 2 && messageArray.length >= 4) {
          payload = messageArray[3]; // CALL payload is at index 3
        } else if (messageArray[0] === 3 && messageArray.length >= 3) {
          payload = messageArray[2]; // CALLRESULT payload is at index 2
        }
      }
    } catch (e) {
      // Ignore parse errors
    }
  }

  if (!payload || typeof payload !== 'object') return null;

  // Extract key information based on message type
  const parts: string[] = [];

  // For CALL_RESULT, show which request it's responding to + connector from matched CALL
  if (data.ocpp_message_type === 3 && allLogs) {
    const matchingCall = findMatchingCall(allLogs, log);
    if (matchingCall) {
      const callAction = matchingCall._source?.data?.ocpp_action;
      const callConnectorId = matchingCall._source?.data?.payload?.connectorId;
      if (callAction) {
        const connectorSuffix = callConnectorId !== undefined ? ` | Connector #${callConnectorId}` : '';
        parts.push(`Response to: ${callAction}${connectorSuffix}`);
      }
    }
  }

  // Common fields
  if (payload.status) parts.push(`Status: ${payload.status}`);
  if (payload.transactionId !== undefined) parts.push(`Transaction ID: ${payload.transactionId}`);
  if (payload.idTag) parts.push(`ID Tag: ${payload.idTag}`);

  // StartTransaction specific
  if (payload.meterStart !== undefined) parts.push(`Meter Start: ${payload.meterStart} Wh`);

  // StopTransaction specific
  if (payload.meterStop !== undefined) parts.push(`Meter Stop: ${payload.meterStop} Wh`);

  // MeterValues specific
  if (payload.meterValue && Array.isArray(payload.meterValue)) {
    const mv = payload.meterValue[0];
    if (mv?.sampledValue) {
      const energy = mv.sampledValue.find((sv: any) => sv.measurand === 'Energy.Active.Import.Register');
      const power = mv.sampledValue.find((sv: any) => sv.measurand === 'Power.Active.Import' && !sv.phase);
      const soc = mv.sampledValue.find((sv: any) => sv.measurand === 'SoC');

      if (energy) parts.push(`Energy: ${energy.value} Wh`);
      if (power) parts.push(`Power: ${(parseFloat(power.value) / 1000).toFixed(2)} kW`);
      if (soc) parts.push(`SoC: ${soc.value}%`);
    }
  }

  // StatusNotification specific
  if (payload.errorCode) parts.push(`Error: ${payload.errorCode}`);
  if (payload.connectorId !== undefined) parts.push(`Connector #${payload.connectorId}`);

  // ChangeConfiguration specific
  if (data.config_key) parts.push(`Config: ${data.config_key}=${data.config_value}`);

  // RemoteStartTransaction / RemoteStopTransaction
  if (data.ocpp_action === 'RemoteStartTransaction' || data.ocpp_action === 'RemoteStopTransaction') {
    if (payload.connectorId !== undefined && parts.findIndex(p => p.includes('Connector #')) === -1) {
      parts.push(`Connector #${payload.connectorId}`);
    }
  }

  return parts.length > 0 ? parts.join(' | ') : null;
};

// Single log entry component
const LogEntry = ({ log, index, allLogs }: { log: any; index: number; allLogs: any[] }) => {
  const translate = useTranslate();
  const [expanded, setExpanded] = useState(false);
  const payloadSummary = getPayloadSummary(log, allLogs);
  const statusInfo = getStatusInfo(log, allLogs);

  return (
    <Accordion
      expanded={expanded}
      onChange={() => setExpanded(!expanded)}
      sx={{
        mb: 1,
        '&:before': { display: 'none' },
        boxShadow: 1,
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        sx={{
          backgroundColor: expanded ? 'action.hover' : 'background.paper',
          '&:hover': { backgroundColor: 'action.hover' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: { xs: 1, sm: 2 } }}>
          <LogLevelIcon level={log.level} />

          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5, flexWrap: 'wrap' }}>
              <Typography variant="caption" color="text.secondary">
                {log.timestamp ? format(new Date(log.timestamp), 'HH:mm:ss.SSS') : '-'}
              </Typography>
              <OcppMessageBadge messageType={log.ocpp_message_type} action={log.ocpp_action} />
              <DirectionBadge direction={log.ocpp_direction} />
              {statusInfo && (
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                  {statusInfo.previous && (
                    <>
                      <ConnectorStatusChip status={statusInfo.previous} />
                      <ArrowForward sx={{ fontSize: 14, color: 'text.secondary' }} />
                    </>
                  )}
                  <ConnectorStatusChip status={statusInfo.current!} />
                </Box>
              )}
            </Box>
            <Typography variant="body2" noWrap>
              {log.message}
            </Typography>
            {payloadSummary && (
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                {payloadSummary}
              </Typography>
            )}
          </Box>

          {log.error_message && (
            <ErrorIcon sx={{ color: 'error.main', fontSize: 20 }} />
          )}
        </Box>
      </AccordionSummary>

      <AccordionDetails sx={{ backgroundColor: 'grey.50', p: { xs: 1.5, sm: 2 } }}>
        <Table size="small">
          <TableBody>
            <TableRow>
              <TableCell width="30%" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>{translate('resources.charging-sessions.show.timestamp')}</TableCell>
              <TableCell>
                {log.timestamp ? format(new Date(log.timestamp), 'PPpp') : '-'}
              </TableCell>
            </TableRow>

            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>{translate('resources.charging-sessions.show.logLevel')}</TableCell>
              <TableCell>
                <Chip label={log.level?.toUpperCase()} size="small" />
              </TableCell>
            </TableRow>

            {log.ocpp_message_type && (
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{translate('resources.charging-sessions.show.messageType')}</TableCell>
                <TableCell>{log.ocpp_message_type}</TableCell>
              </TableRow>
            )}

            {log.ocpp_action && (
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{translate('resources.charging-sessions.show.ocppAction')}</TableCell>
                <TableCell>{log.ocpp_action}</TableCell>
              </TableRow>
            )}

            {log.ocpp_direction && (
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{translate('resources.charging-sessions.show.direction')}</TableCell>
                <TableCell>
                  <DirectionBadge direction={log.ocpp_direction} />
                </TableCell>
              </TableRow>
            )}

            {log.resource && (
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{translate('resources.charging-sessions.show.resource')}</TableCell>
                <TableCell>
                  <Box>
                    {log.resource.type && <Typography variant="body2">{translate('resources.charging-sessions.show.type')}: {log.resource.type}</Typography>}
                    {log.resource.id && <Typography variant="body2">{translate('resources.charging-sessions.show.id')}: {log.resource.id}</Typography>}
                    {log.resource.name && <Typography variant="body2">{translate('resources.charging-sessions.show.name')}: {log.resource.name}</Typography>}
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {log.error_message && (
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{translate('resources.charging-sessions.show.error')}</TableCell>
                <TableCell>
                  <Alert severity="error" sx={{ mt: 1 }}>
                    {log.error_message}
                  </Alert>
                </TableCell>
              </TableRow>
            )}

            {log.http?.request && (
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{translate('resources.charging-sessions.show.request')}</TableCell>
                <TableCell>
                  <Box
                    component="pre"
                    sx={{
                      p: 1,
                      backgroundColor: 'grey.100',
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 300,
                      maxWidth: '100%',
                      wordBreak: 'break-all',
                      fontSize: '0.75rem',
                    }}
                  >
                    {JSON.stringify(log.http.request, null, 2)}
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {log.http?.response && (
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{translate('resources.charging-sessions.show.response')}</TableCell>
                <TableCell>
                  <Box
                    component="pre"
                    sx={{
                      p: 1,
                      backgroundColor: 'grey.100',
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 300,
                      maxWidth: '100%',
                      wordBreak: 'break-all',
                      fontSize: '0.75rem',
                    }}
                  >
                    {JSON.stringify(log.http.response, null, 2)}
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {log.tags && log.tags.length > 0 && (
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{translate('resources.charging-sessions.show.tags')}</TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {log.tags.map((tag: string, idx: number) => (
                      <Chip key={idx} label={tag} size="small" variant="outlined" />
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
            )}

            {log._source?.data?.ocpp_message_array && (
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', verticalAlign: 'top' }}>OCPP Message</TableCell>
                <TableCell>
                  <OcppMessageSection messageArray={log._source.data.ocpp_message_array} />
                </TableCell>
              </TableRow>
            )}

            {log._source && (
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold' }}>{translate('resources.charging-sessions.show.fullLogData')}</TableCell>
                <TableCell>
                  <Box
                    component="pre"
                    sx={{
                      p: 1,
                      backgroundColor: 'grey.100',
                      borderRadius: 1,
                      overflow: 'auto',
                      maxHeight: 400,
                      maxWidth: '100%',
                      wordBreak: 'break-all',
                      fontSize: '0.75rem',
                    }}
                  >
                    {JSON.stringify(log._source, null, 2)}
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </AccordionDetails>
    </Accordion>
  );
};

// Main OCPP Logs Component
export const OcppLogs = () => {
  const record = useRecordContext();
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const translate = useTranslate();

  const [logs, setLogs] = useState<any[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [perPage] = useState(50);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!record?.id) return;

    const fetchLogs = async () => {
      setLoading(true);
      setError(null);

      try {
        console.log('[OcppLogs] Fetching logs for session:', record.id, 'page:', page);
        const response = await dataProvider.getOne('charging-sessions', {
          id: `${record.id}/ocpp-logs?page=${page}&per_page=${perPage}`,
        });

        console.log('[OcppLogs] Response received:', response);
        console.log('[OcppLogs] Response.data:', response.data);
        console.log('[OcppLogs] Response.data.data:', response.data?.data);
        console.log('[OcppLogs] Response.data.total:', response.data?.total);

        if (response.data) {
          const logsData = response.data.data || [];
          const totalCount = response.data.total || 0;
          console.log('[OcppLogs] Setting logs:', logsData.length, 'total:', totalCount);
          setLogs(logsData);
          setTotal(totalCount);
        }
      } catch (err: any) {
        console.error('Failed to fetch OCPP logs:', err);
        setError(err.message || 'Failed to fetch OCPP logs');
        notify('Failed to fetch OCPP logs', { type: 'error' });
      } finally {
        setLoading(false);
      }
    };

    fetchLogs();
  }, [record?.id, page, perPage, dataProvider, notify]);

  if (!record) return null;

  return (
    <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 2, sm: 3 } }}>
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              backgroundColor: 'info.light',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mr: 2,
            }}
          >
            <BatteryChargingFull sx={{ color: 'info.main' }} />
          </Box>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
              {translate('resources.charging-sessions.show.ocppTransactionLogs')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {translate('resources.charging-sessions.show.completeOcppProtocol')}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : logs.length === 0 ? (
          <Alert severity="info">
            {translate('resources.charging-sessions.show.noOcppLogsFound')}
            <ul>
              <li>{translate('resources.charging-sessions.show.noOcppLogsReason1')}</li>
              <li>{translate('resources.charging-sessions.show.noOcppLogsReason2')}</li>
              <li>{translate('resources.charging-sessions.show.noOcppLogsReason3')}</li>
            </ul>
          </Alert>
        ) : (
          <>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                {translate('resources.charging-sessions.show.showingLogEntries')} {logs.length} {translate('resources.charging-sessions.show.of')} {total} {translate('resources.charging-sessions.show.logEntries')}
              </Typography>
            </Box>

            {logs.map((log, index) => (
              <LogEntry key={log.id || index} log={log} index={index} allLogs={logs} />
            ))}

            {total > perPage && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 2, sm: 3 } }}>
                <Stack spacing={2}>
                  <Pagination
                    count={Math.ceil(total / perPage)}
                    page={page}
                    onChange={(e, value) => setPage(value)}
                    color="primary"
                    showFirstButton
                    showLastButton
                  />
                </Stack>
              </Box>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default OcppLogs;
